// Which messages vue-i18n starts with.
//
// English is bundled: it is the fallback for every key, so it has to be there
// before anything renders, and being part of the JS means it is cached across
// pages. The visitor's own locale comes from the page, published by Rails the
// same way the locale itself is — `window.hiikerMessages`.
//
// The alternative was bundling all eight, which after the Vue extraction meant
// roughly 850KB of JSON in every entrypoint to serve the one locale the visitor
// reads. Loading the active locale asynchronously instead would render English
// first and swap, which is a worse thing to show someone than a slightly larger
// page.
//
// Pure and dependency-free so it can be unit-tested without a DOM.

export type Messages = Record<string, unknown>;

// Accepts whatever was on `window`. Anything malformed is ignored rather than
// thrown: a bad payload should cost a translation, not the page's JavaScript.
export const parseMessages = (raw: unknown): Messages | null => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

  const messages = raw as Messages;
  return Object.keys(messages).length > 0 ? messages : null;
};

export const buildMessages = (
  english: Messages,
  published: unknown,
  locale: string
): Record<string, Messages> => {
  const messages: Record<string, Messages> = { en: english };
  if (locale === 'en') return messages;

  const parsed = parseMessages(published);
  // No payload — vue-i18n falls back to English per key, which is what a page
  // that never published its messages should show.
  if (parsed) messages[locale] = parsed;

  return messages;
};
